import { useForgotPassword } from '@hooks/postQueries';
import AuthFormLayout from '@layouts/form/AuthFormLayout';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ResetPassword = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const otp = Array.isArray(location.state?.otp)
    ? location.state.otp
    : String(location.state?.otp || '').split('');
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      new_password: '',
      confirm_password: '',
    },
  });

  const newPassword = watch('new_password');

  const validatePassword = (value) => {
    if (!value) return 'Password is required';
    if (value.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(value)) return 'Must contain at least one uppercase letter';
    if (!/[a-z]/.test(value)) return 'Must contain at least one lowercase letter';
    if (!/\d/.test(value)) return 'Must contain at least one number';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) return 'Must contain at least one special character';
    return true;
  };

  const validateConfirmPassword = (value) => {
    if (!value) return 'Please confirm your password';
    if (value !== newPassword) return 'Passwords do not match';
    return true;
  };

  const { mutateAsync: resetPassword, isPending } = useForgotPassword();

  const onSubmit = async (data) => {
    try {
      if (!email || !otp) {
        throw new Error('Email or OTP is missing');
      }

      await resetPassword({
        email,
        otp: otp.join(''),
        purpose: 'forget_password',
        new_password: data.new_password,
      });
      setTimeout(() => {
        toast.success('Password updated successfully!, Redirecting to login page');
      }, 100);
      setTimeout(() => {
        navigate('/signin');
      }, 4000);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to update password';

      setTimeout(() => {
        toast.error(errorMessage);
      }, 100);
    }
  };

  useEffect(() => {
    if (!otp || !email) {
      navigate('/forget-password', { state: { email, otp } });
    }
  }, []);

  return (
    <AuthFormLayout
      title="Update your password!"
      text="Create a new password to access your account"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <FormControl fullWidth>
            <FormLabel>New Password</FormLabel>
            <Controller
              name="new_password"
              control={control}
              rules={{ validate: validatePassword }}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  placeholder="New Password"
                  type={showPassword ? 'text' : 'password'}
                  error={!!errors.new_password}
                  helperText={errors.new_password?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth error={!!errors.confirm_password}>
            <FormLabel>Confirm Password</FormLabel>
            <Controller
              name="confirm_password"
              control={control}
              rules={{
                required: 'Please confirm your password',
                validate: validateConfirmPassword,
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Confirm Password"
                  variant="outlined"
                  type={showConfirmPassword ? 'text' : 'password'}
                  error={!!errors.confirm_password}
                  helperText={errors.confirm_password?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </FormControl>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isPending}
            sx={{ mt: 2 }}
          >
            {isPending ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
          </Button>
        </Stack>
      </form>
    </AuthFormLayout>
  );
};

export default ResetPassword;
